import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import useHttpHook from "../../Hooks/HttpHook";
import { getMerchBaseUrl } from "../../utils/apiBase";
import { exchangeEventSsoToken } from "../../utils/eventSsoLogin";
import Header from "../Layout/Header";
import EventFooter from "../Layout/V2/EventFooter";
import { DEFAULT_EVENT_DOCUMENT_TITLE } from "../Layout/V2/eventPageHeadPresets";
import { readQueryLoginToken } from "../../utils/Helper";
import { showErrorToast } from "../../utils/notify";

const INVALID_USERNAMES = new Set([
  "",
  "undefined",
  "null",
  "true",
  "false",
  "nan",
]);

const loginHomeUrl = () => `${getMerchBaseUrl()}/login?platform=event`;

/** In-app path only, so ?redirect= cannot leave this site. */
const safeInternalPath = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return "";
  try {
    const url = new URL(trimmed, "http://localhost");
    url.searchParams.delete("token");
    url.searchParams.delete("Token");
    url.searchParams.delete("authId");
    const search = url.searchParams.toString();
    return `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
  } catch {
    return "";
  }
};

const isSafeEventReturnPath = (path) => {
  if (!path || typeof path !== "string") return false;
  const normalized = path.trim();
  if (!normalized.startsWith("/") || normalized.startsWith("//")) {
    return false;
  }
  const lower = normalized.toLowerCase();
  if (lower.startsWith("/auth/")) return false;
  return true;
};

const normalizeUserName = (value) => {
  if (value == null) return "";
  const raw = Array.isArray(value) ? value[0] : value;
  return String(raw).trim();
};

/** Profile username from ?userName= (Social → Event handoff). */
const resolveVisitorUserName = (rawUserName, authToken) => {
  const normalized = normalizeUserName(rawUserName);
  if (!normalized) return "";
  if (INVALID_USERNAMES.has(normalized.toLowerCase())) return "";
  const token = normalizeUserName(authToken);
  if (token && normalized === token) return "";
  return normalized;
};

/** Path token from /Auth/:Token or /Auth/part1/part2 (base64 slashes). */
const readPathLoginToken = (params) => {
  const raw = params?.["*"] || params?.Token || params?.token || "";
  return String(raw).replace(/\/+$/, "").replace(/ /g, "+").trim();
};

const Auth = () => {
  const navigate = useNavigate();
  const [loading, sendRequest] = useHttpHook();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const authToken =
    readPathLoginToken(params) || readQueryLoginToken(searchParams.toString());
  const startedRef = useRef(false);
  const toastedRef = useRef(new Set());
  const [error, setError] = useState(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = error ? "Sign-in failed" : "Authenticating...";
    document.body.classList.add(
      "dashboard-page",
      "event-home-page",
      "event-auth-page"
    );
    return () => {
      document.body.classList.remove(
        "dashboard-page",
        "event-home-page",
        "event-auth-page"
      );
      document.title =
        previousTitle &&
        previousTitle !== "Authenticating..." &&
        previousTitle !== "Sign-in failed"
          ? previousTitle
          : DEFAULT_EVENT_DOCUMENT_TITLE;
    };
  }, [error]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!authToken) {
      showAuthError("Missing sign-in token.");
      return;
    }
    login(authToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Match classes: only bounce to platform login after showing a clear error.
  useEffect(() => {
    if (!error) return undefined;
    const isUnauthorized = /unauth/i.test(String(error));
    if (!isUnauthorized) return undefined;
    const timer = setTimeout(() => {
      window.location.href = loginHomeUrl();
    }, 2500);
    return () => clearTimeout(timer);
  }, [error]);

  const showAuthError = (message) => {
    const text =
      message || "Couldn’t complete sign-in. Please try again.";
    setError(text);
    if (toastedRef.current.has(text)) return;
    toastedRef.current.add(text);
    showErrorToast(text);
  };

  /**
   * Land inside Event hub (never merch root).
   * Visitor handoff from Social uses ?userName= so sidebar/profile stay in sync.
   */
  const continueAfterLogin = (sessionUser) => {
    const returnTo = searchParams.get("returnTo");
    if (isSafeEventReturnPath(returnTo)) {
      navigate(returnTo, { replace: true });
      return;
    }

    const visitorUserName = resolveVisitorUserName(
      searchParams.get("userName"),
      authToken
    );
    const loggedInUserName = normalizeUserName(sessionUser?.userName);
    const viewingOtherProfile =
      visitorUserName &&
      loggedInUserName &&
      visitorUserName.toLowerCase() !== loggedInUserName.toLowerCase();

    let redirect = safeInternalPath(searchParams.get("redirect"));
    if (redirect && viewingOtherProfile) {
      const url = new URL(redirect, "http://localhost");
      if (!url.searchParams.get("userName")) {
        url.searchParams.set("userName", visitorUserName);
      }
      const search = url.searchParams.toString();
      redirect = `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
    }
    if (redirect) {
      navigate(redirect, { replace: true });
      return;
    }

    if (
      visitorUserName &&
      loggedInUserName &&
      visitorUserName.toLowerCase() !== loggedInUserName.toLowerCase()
    ) {
      navigate(`/?userName=${encodeURIComponent(visitorUserName)}`, {
        replace: true,
      });
      return;
    }

    navigate("/", { replace: true });
  };

  const login = async (token) => {
    try {
      const result = await exchangeEventSsoToken(sendRequest, dispatch, token);
      if (result.ok) {
        continueAfterLogin(result.data?.userDetails);
        return;
      }
      showAuthError(result.message);
    } catch (err) {
      console.log({ err });
      showAuthError(
        err?.message || "Couldn’t complete sign-in. Please try again."
      );
    }
  };

  return (
    <div className="event-auth-shell d-flex flex-column">
      <Header />
      <main className="dash-wrap dash-v2 event-wrap flex-grow-1 d-flex flex-column">
        <div className="auth-page-body">
          {!error ? (
            <div className="auth-page-spinner" role="status" aria-live="polite">
              <div className="spinner-border text-primary" aria-hidden="true" />
              <span className="sr-only">
                {loading ? "Authenticating…" : "Getting things ready…"}
              </span>
            </div>
          ) : (
            <div className="auth-page-error" role="alert">
              <p className="auth-page-error-title">Couldn’t sign you in</p>
              <p className="auth-page-error-text">{error}</p>
              <a className="btn btn-primary btn-sm px-3" href={loginHomeUrl()}>
                Back to login
              </a>
            </div>
          )}
        </div>
      </main>
      <EventFooter />
    </div>
  );
};

export default Auth;
